/**
 * QUIPWA — SafeBackground
 *
 * The full-screen background layer behind the app: a colour, a pattern over a
 * colour, or an image from the user's Drive (with a fallback if it cannot be
 * loaded — offline, revoked, too slow).
 *
 * Patterns are drawn with `--pattern-ink`, derived from the theme's text
 * colour, so they show on light themes too (they were fixed white at 5 %, and
 * the pattern type ignored the chosen colour altogether).
 *
 * Only opacity animates: the old `transition: all` on a blurred full-screen
 * layer repainted the whole screen on every theme change.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { driveService } from '../../lib/drive/drive-service'
import type { PatternStyle } from '../../lib/theme/theme-store'
import '../../styles/SafeBackground.css'

interface SafeBackgroundProps {
  type: 'solid' | 'gradient' | 'pattern' | 'image'
  value: string
  patternStyle: PatternStyle
  opacity: number
  blur: number
  children: ReactNode
}

const IMAGE_TIMEOUT_MS = 10_000

export function SafeBackground({ type, value, patternStyle, opacity, blur, children }: SafeBackgroundProps) {
  const [bgUrl, setBgUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [priorKey, setPriorKey] = useState(`${type}:${value}`)
  // The latest bgUrl, so cleanup revokes the most recent object URL.
  const bgUrlRef = useRef(bgUrl)
  useEffect(() => {
    bgUrlRef.current = bgUrl
  }, [bgUrl])

  // Reset image state when the background changes (during render, not in an effect).
  const key = `${type}:${value}`
  if (key !== priorKey) {
    setPriorKey(key)
    setBgUrl(null)
    setError(false)
  }

  useEffect(() => {
    if (type !== 'image' || !value) return
    let isMounted = true
    setLoading(true) // eslint-disable-line react-hooks/set-state-in-effect

    const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Timeout loading background')), IMAGE_TIMEOUT_MS))
    Promise.race([driveService.getAssetUrl(value), timeout])
      .then((url) => {
        if (!isMounted) return URL.revokeObjectURL(url)
        setBgUrl(url)
        setLoading(false)
      })
      .catch((err: unknown) => {
        console.warn('[QUIPWA] Failed to load background image:', err)
        if (!isMounted) return
        setError(true)
        setLoading(false)
      })

    return () => {
      isMounted = false
      if (bgUrlRef.current) URL.revokeObjectURL(bgUrlRef.current)
    }
  }, [type, value])

  const layerStyle = (): CSSProperties => {
    const base: CSSProperties = { opacity, filter: blur > 0 ? `blur(${blur}px)` : undefined }
    if (error) return { ...base, backgroundColor: 'var(--c-bg)' } // .safe-bg-layer--fallback draws dots
    if (type === 'image') {
      return bgUrl ? { ...base, backgroundColor: 'var(--c-bg)', backgroundImage: `url(${bgUrl})` } : base
    }
    // A gradient is a whole `background`; colour and pattern only set the
    // colour, so the pattern class keeps its background-image.
    return type === 'gradient' ? { ...base, background: value } : { ...base, backgroundColor: value }
  }

  const classes = [
    'safe-bg-layer',
    type === 'pattern' && patternStyle !== 'none' ? `pattern-${patternStyle}` : '',
    type === 'image' && bgUrl ? 'safe-bg-layer--image' : '',
    error ? 'safe-bg-layer--fallback' : '',
    loading ? 'is-loading' : '',
  ].filter(Boolean).join(' ')

  return (
    <div className="safe-bg-container">
      <div className={classes} style={layerStyle()} aria-hidden="true" />
      <div className="safe-bg-content">{children}</div>
    </div>
  )
}
